/**
 * Auth abstraction — the ONLY file (besides `main.tsx`, which mounts the
 * provider) allowed to import `@clerk/react`. Every other component reads
 * auth state through {@link useAppAuth} and never touches Clerk directly,
 * so the app has exactly one seam to swap or stub auth.
 *
 * Mode is chosen at build time from `VITE_CLERK_PUBLISHABLE_KEY`:
 *  - present → "clerk": real `ClerkProvider`, real sign-in/out.
 *  - absent  → "dev": a passthrough provider. `isSignedIn` is always
 *    true, `userId` is the fixed string `"dev-user"`, `getToken` always
 *    resolves `null`. `TrpcProvider` treats a `null` token as "dev mode"
 *    and sends the `x-dev-user: dev-user` header instead of a Bearer
 *    token, matching the backend's `AUTH_MODE=dev` shortcut
 *    (`projects/hindi/backend/src/context.ts`).
 */
import {
  ClerkProvider,
  SignInButton as ClerkSignInButton,
  useAuth as useClerkAuth,
  useClerk,
} from "@clerk/react";
import { createContext, useContext, useMemo, type ReactNode } from "react";

export type AuthMode = "clerk" | "dev";

export interface AppAuth {
  mode: AuthMode;
  isSignedIn: boolean;
  userId: string | null;
  getToken(): Promise<string | null>;
  signOut(): void;
}

const DEV_USER_ID = "dev-user";

const AppAuthContext = createContext<AppAuth | null>(null);

const clerkPublishableKey: string | undefined = import.meta.env
  .VITE_CLERK_PUBLISHABLE_KEY;

/** `true` when a Clerk publishable key was baked in at build time. */
export const authMode: AuthMode = clerkPublishableKey ? "clerk" : "dev";

function DevAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AppAuth>(
    () => ({
      mode: "dev",
      isSignedIn: true,
      userId: DEV_USER_ID,
      getToken: () => Promise.resolve(null),
      signOut: () => {
        // No-op in dev mode — there is no session to end.
      },
    }),
    [],
  );

  return (
    <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
  );
}

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, getToken } = useClerkAuth();
  const { signOut } = useClerk();

  const value = useMemo<AppAuth>(
    () => ({
      mode: "clerk",
      isSignedIn: isLoaded === true && isSignedIn === true,
      userId: userId ?? null,
      getToken: async () => (isLoaded && isSignedIn ? getToken() : null),
      signOut: () => void signOut(),
    }),
    [isLoaded, isSignedIn, userId, getToken, signOut],
  );

  return (
    <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
  );
}

/**
 * Wraps the app in whichever provider matches the build-time auth mode.
 * In clerk mode, this owns the single `ClerkProvider` — no other file
 * should render one.
 */
export function AppAuthProvider({ children }: { children: ReactNode }) {
  if (!clerkPublishableKey) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }

  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      appearance={{
        variables: {
          colorBackground: "#f6f1e7",
          colorDanger: "#b3362b",
          colorForeground: "#221d18",
          colorInputBackground: "#efe7d8",
          colorInputForeground: "#221d18",
          colorPrimary: "#221d18",
          colorText: "#221d18",
          fontFamily: '"Newsreader Variable", Georgia, serif',
        },
      }}
    >
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProvider>
  );
}

/** The single seam every component uses to read auth state — never import Clerk hooks directly. */
export function useAppAuth(): AppAuth {
  const ctx = useContext(AppAuthContext);
  if (ctx === null) {
    throw new Error("useAppAuth must be used within AppAuthProvider");
  }
  return ctx;
}

/**
 * A ready-styled sign-in trigger for the landing gate. Wraps Clerk's
 * modal `SignInButton` so the landing screen doesn't import `@clerk/react`
 * itself. In dev mode there is nothing to sign into, so the children
 * render bare (inert) — this keeps the `?landing-preview=1` dev preview
 * (see `Router`) visually faithful to the real clerk-mode landing.
 */
export function AppSignInButton({ children }: { children: ReactNode }) {
  if (authMode === "dev") return <>{children}</>;
  return <ClerkSignInButton mode="modal">{children}</ClerkSignInButton>;
}
